// Story scope — which tracked situation the cockpit is currently looking at.
//
// A story is a plain server row (migration 028) and membership is a versioned
// `entity_version.story_id`. TENANCY IS ALREADY ENFORCED SERVER-SIDE, inside
// can_read: anything this client can see it is allowed to see. So nothing in
// this module is an access control — it is the display half, a filter over
// what the server already handed us, in the same spirit as the layer toggles.
//
// Three rules the rest of the SPA leans on:
//   * Unfiled is universal. `storyId` null — '' in the tile property, which is
//     how mvt_entities spells "no story" — is in scope under EVERY selection.
//     Nothing is filed on day one, so a selection that hid unfiled objects
//     would render an empty map and read as a broken feature. "Only this
//     story" turns that off deliberately, per operator.
//   * The subtree is expanded HERE from the flat list's parentId. Viewing a
//     parent unions in its descendants, and /api/stories returns a flat list
//     precisely so a switch costs no round trip.
//   * The selection lives in localStorage like every other preference, NOT in
//     the session. Tile URLs must not depend on invisible server-side state or
//     the same URL stops meaning the same bytes — which is exactly what
//     Martin's cache and the byte-identical asyncpg fallback rely on.
//   * A browser that has never chosen opens on the operator's DEFAULT story
//     (setting `defaultStory`, usually a group such as Gulf over Iran / Gulf
//     and Yemen / Gulf). "All stories" is therefore a choice that has to be
//     REMEMBERED — stored as ALL_STORIES — or picking it would be undone by
//     the default on the next load.

import { get } from './api';
import type { StoryDto } from './api';

const STORY_KEY = 'cockpit-story';
const ONLY_KEY = 'cockpit-story-only';
/** The stored value for an explicit "All stories" — distinct from no value at
 *  all, which means "never chosen, use the default". */
const ALL_STORIES = '*';

let stories: StoryDto[] = [];
const byId = new Map<string, StoryDto>();
let activeId: string | null = null;
let onlyThis = false;
let listeners: ((storyId: string | null) => void)[] = [];

// Memoised active subtree: inScope() runs once per entity over a list capped at
// 10000, so it must not walk the story list each call.
let scope: Set<string> | null = null;

export interface StoryNode {
  story: StoryDto;
  /** Generations below a root — an indent for the picker, nothing more. */
  depth: number;
}

/** Fetch the stories this principal can see and restore the saved selection,
 *  or open on `defaultId` (the operator's `defaultStory`) when this browser has
 *  never chosen. Never throws: an unreachable /api/stories must cost the story
 *  picker, not the whole cockpit. */
export async function loadStories(defaultId?: string | null): Promise<StoryDto[]> {
  // renderApp is re-entered on every login and the callback registrations in
  // this codebase are all one-off and unsubscribable, so a surviving listener
  // list would double the work per story switch on each re-login and keep
  // calling into a torn-down DOM.
  listeners = [];
  try {
    // The route envelopes the list ({"stories": [...]}) like every other
    // collection endpoint here. Unwrap defensively rather than trusting the
    // generic: `get<T>` asserts a shape, it does not verify one, so a mismatch
    // is invisible to tsc and lands as a "not iterable" throw during boot.
    const dto = await get<{ stories?: StoryDto[] } | StoryDto[]>('/api/stories');
    const list = Array.isArray(dto) ? dto : dto?.stories;
    stories = Array.isArray(list) ? list : [];
  } catch {
    stories = [];
  }
  byId.clear();
  for (const s of stories) byId.set(s.id, s);
  onlyThis = localStorage.getItem(ONLY_KEY) === '1';
  activeId = initialSelection(localStorage.getItem(STORY_KEY), defaultId);
  scope = null;
  return stories;
}

/** Which story a load opens on. A saved choice wins, "All stories" included;
 *  never having chosen takes the default. A saved story that is now archived,
 *  or no longer visible to this principal, would scope the map to something
 *  the picker cannot show and offer no way back except a guess — that falls
 *  back to "All stories", which only ever widens what is on screen, and so
 *  does a default this principal cannot see. The key is left alone: a
 *  transient fetch failure must not erase the operator's choice. */
function initialSelection(saved: string | null, defaultId?: string | null): string | null {
  const usable = (id: string | null | undefined): string | null => {
    const s = id ? byId.get(id) : undefined;
    return s && s.status !== 'archived' ? s.id : null;
  };
  if (saved === ALL_STORIES) return null;
  if (saved) return usable(saved);
  return usable(defaultId);
}

/** Every story the server was willing to list, archived ones included — the
 *  "Filed under" badge still has to name a retired story. */
export function getStories(): StoryDto[] {
  return stories.slice();
}

export function getStory(id: string | null | undefined): StoryDto | null {
  return (id && byId.get(id)) || null;
}

export function getActiveStory(): StoryDto | null {
  return activeId ? byId.get(activeId) ?? null : null;
}

/** Switch scope. An unknown id resolves to "All stories" rather than to an
 *  empty map. Notifies only on a real change. */
export function setStory(id: string | null): void {
  const next = id && byId.has(id) ? id : null;
  if (next === activeId) return;
  activeId = next;
  scope = null;
  localStorage.setItem(STORY_KEY, next ?? ALL_STORIES);
  notify();
}

/** Whether unfiled objects are hidden along with the other stories'. Off by
 *  default — see the header: the open default is what keeps a young story from
 *  looking broken. */
export function isOnlyThisStory(): boolean {
  return onlyThis;
}

export function setOnlyThisStory(on: boolean): void {
  if (on === onlyThis) return;
  onlyThis = on;
  localStorage.setItem(ONLY_KEY, on ? '1' : '0');
  notify();
}

/** Subscribers are called for a story switch AND for the "only this story"
 *  toggle, because both change what is in scope; re-derive rather than diffing
 *  against the argument. Unsubscribing is deliberately absent — loadStories()
 *  clears the list, which is the only lifecycle this app has. */
export function onStoryChange(fn: (storyId: string | null) => void): void {
  listeners.push(fn);
}

/** A story plus every descendant, expanded from the flat list's parentId.
 *  Visited-guarded: parent cycles are refused by app/stories/service.py, not by
 *  the database, so a hand-INSERTed one must not hang the map. */
export function subtreeIds(id: string | null | undefined): string[] {
  if (!id || !byId.has(id)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const queue: string[] = [id];
  while (queue.length) {
    const cur = queue.shift()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    out.push(cur);
    for (const s of stories) if (s.parentId === cur) queue.push(s.id);
  }
  return out;
}

/** The selected story and its descendants — the literal list a tile `in`
 *  expression matches against. Empty under "All stories". */
export function activeSubtreeIds(): string[] {
  return activeId ? [...ensureScope()] : [];
}

/** The one scope test. Give it an entity's storyId or a tile's `story`
 *  property; '' and null both mean unfiled. */
export function inScope(storyId: string | null | undefined): boolean {
  if (!activeId) return true;
  if (!storyId) return !onlyThis;
  return ensureScope().has(storyId);
}

/** Picker order: roots by name, each followed by its own children, so `depth`
 *  is an indent. Archived stories are omitted — retired things are hidden, and
 *  a selection the operator cannot see again is a trap. */
export function orderedStories(): StoryNode[] {
  return storyTree(stories);
}

/** orderedStories() over any list — the admin console orders the stories it
 *  fetched itself the same way, so its pickers read like the cockpit's. */
export function storyTree(list: StoryDto[]): StoryNode[] {
  const live = list.filter((s) => s.status !== 'archived');
  const ids = new Set(live.map((s) => s.id));
  const kids = new Map<string, StoryDto[]>();
  const roots: StoryDto[] = [];
  for (const s of live) {
    // A child whose parent is archived, or invisible to this principal, is
    // listed as a root instead of vanishing with it.
    const parent = s.parentId && ids.has(s.parentId) ? s.parentId : null;
    if (!parent) { roots.push(s); continue; }
    const arr = kids.get(parent);
    if (arr) arr.push(s);
    else kids.set(parent, [s]);
  }
  const byName = (a: StoryDto, b: StoryDto) => a.name.localeCompare(b.name);
  const out: StoryNode[] = [];
  const seen = new Set<string>();
  const walk = (list: StoryDto[], depth: number): void => {
    for (const s of [...list].sort(byName)) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      out.push({ story: s, depth });
      walk(kids.get(s.id) ?? [], depth + 1);
    }
  };
  walk(roots, 0);
  // A parent cycle leaves its members unreachable from every root. List them
  // flat rather than dropping them off the picker entirely.
  for (const s of live.sort(byName)) if (!seen.has(s.id)) out.push({ story: s, depth: 0 });
  return out;
}

function ensureScope(): Set<string> {
  if (!scope) scope = new Set(subtreeIds(activeId));
  return scope;
}

function notify(): void {
  for (const fn of listeners) fn(activeId);
}
